import React, { useMemo, useRef, useState } from "react";
import Select from "react-select";
import { msmartAxios } from "../../../../api/axios";
import { getErrorMessage } from "../../../../utils/errorMessage";

export default function BulkUploadWizard({
  open,
  onClose,
  template,
  teamId,
  userToken,
  notifyError,
  notifySuccess,
  onDone,
}) {
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadStep, setUploadStep] = useState("select_mode"); // select_mode | mapping | preview | summary
  const [uploadMode, setUploadMode] = useState("easy");
  const [isProcessing, setIsProcessing] = useState(false);

  const [previewData, setPreviewData] = useState([]);
  const [finalReport, setFinalReport] = useState(null);

  const [editingPreviewRowId, setEditingPreviewRowId] = useState(null);
  const originalPhonesRef = useRef(new Map());

  const [fileHeaders, setFileHeaders] = useState([]);
  const [phoneFormat, setPhoneFormat] = useState("single");

  const [fieldMapping, setFieldMapping] = useState({
    name: "",
    phone_number_full: "",
    country: "",
    phone: "",
    status: "",
    remark: "",
    followUpDate: "",
    followUpTime: "",
  });

  const resetUploadModal = () => {
    setUploadStep("select_mode");
    setBulkFile(null);
    setPreviewData([]);
    setFinalReport(null);
    setIsProcessing(false);
    setEditingPreviewRowId(null);
    originalPhonesRef.current = new Map();
    setFileHeaders([]);
    setUploadMode("easy");
    setPhoneFormat("single");
    setFieldMapping({
      name: "",
      phone_number_full: "",
      country: "",
      phone: "",
      status: "",
      remark: "",
      followUpDate: "",
      followUpTime: "",
    });
  };

  const closeAll = () => {
    resetUploadModal();
    onClose();
  };

  const previewCounts = useMemo(() => {
    const counts = { new: 0, duplicate: 0, error: 0 };
    for (const item of previewData) {
      if (counts.hasOwnProperty(item.status)) counts[item.status]++;
    }
    return counts;
  }, [previewData]);

  const handleGetHeaders = async () => {
    if (!bulkFile) {
      notifyError("Please select a file.");
      return;
    }
    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", bulkFile);

    try {
      const response = await msmartAxios.post(
        "/api/msmart/bulk-upload/get-headers",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            accessToken: userToken,
          },
        }
      );

      setFileHeaders(response.data.headers);
      setUploadStep("mapping");
    } catch (err) {
      notifyError(getErrorMessage(err, "Could not get file headers."));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAnalyzeFile = async () => {
    if (!bulkFile) {
      notifyError("Please select a file.");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", bulkFile);
    formData.append("teamId", teamId);
    formData.append("mode", uploadMode);

    if (uploadMode === "advanced") {
      // validate phone mapping
      if (phoneFormat === "single") {
        if (!fieldMapping.phone_number_full) {
          notifyError("You must map the full phone number field.");
          setIsProcessing(false);
          return;
        }
      } else {
        if (!fieldMapping.country || !fieldMapping.phone) {
          notifyError("You must map the country & phone fields.");
          setIsProcessing(false);
          return;
        }
      }

      formData.append("mapping", JSON.stringify(fieldMapping));
      formData.append("phoneFormat", phoneFormat);
    }

    try {
      const response = await msmartAxios.post(
        "/api/msmart/bulk-upload/analyze",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            accessToken: userToken,
          },
        }
      );

      const priority = { error: 1, duplicate: 2, new: 3 };
      const sortedData = response.data.previewData.sort(
        (a, b) => priority[a.status] - priority[b.status]
      );

      const processedPreview = sortedData.map((item) => ({
        ...item,
        action: item.status === "duplicate" ? "skip" : null,
        editData: { ...item.newData },
      }));

      setPreviewData(processedPreview);
      setUploadStep("preview");

      // store original phones map
      const phoneMap = new Map();
      processedPreview.forEach((x) => {
        if (x.originalData) phoneMap.set(String(x.originalData.phone), x.originalData);
      });
      originalPhonesRef.current = phoneMap;
    } catch (err) {
      notifyError(getErrorMessage(err, "Failed to analyze the file."));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDuplicateActionChange = (rowIndex, action) => {
    const updated = [...previewData];
    updated[rowIndex].action = action;
    setPreviewData(updated);
  };

  const handlePreviewRowChange = (index, field, value) => {
    const updated = [...previewData];
    updated[index].editData[field] = value;
    setPreviewData(updated);
  };

  const handleSavePreviewRow = (index) => {
    const updated = [...previewData];
    const editedItem = updated[index];

    const sanitizedPhone = String(editedItem.editData.phone || "").replace(/\D/g, "");
    const sanitizedCountry = String(editedItem.editData.country || "").replace(/\D/g, "");

    if (!sanitizedPhone) {
      editedItem.status = "error";
      editedItem.errorReason = "Phone number cannot be empty.";
    } else if (originalPhonesRef.current.has(sanitizedPhone)) {
      editedItem.status = "duplicate";
      editedItem.action = "skip";
      editedItem.errorReason = "";
    } else {
      editedItem.status = "new";
      editedItem.action = null;
      editedItem.errorReason = "";
    }

    editedItem.newData = {
      ...editedItem.newData,
      ...editedItem.editData,
      phone: sanitizedPhone,
      country: sanitizedCountry,
    };

    const priority = { error: 1, duplicate: 2, new: 3 };
    updated.sort((a, b) => priority[a.status] - priority[b.status]);

    setPreviewData(updated);
    setEditingPreviewRowId(null);
  };

  const handleExecuteUpload = async () => {
    setIsProcessing(true);

    const leadsToProcess = previewData
      .filter((item) => item.status !== "error")
      .map((item) => {
        if (item.status === "new") return { action: "create", data: item.newData };
        if (item.status === "duplicate") {
          return {
            action: item.action,
            data: item.newData,
            originalData: item.originalData,
          };
        }
        return { action: "skip" };
      });

    try {
      const response = await msmartAxios.post(
        "/api/msmart/bulk-upload/execute",
        { teamId, leadsToProcess },
        { headers: { accessToken: userToken } }
      );

      setFinalReport(response.data);
      setUploadStep("summary");
      notifySuccess("Bulk upload completed");

      if (onDone) onDone();
    } catch (err) {
      notifyError(getErrorMessage(err, "Failed to save data."));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProceed = () => {
    if (uploadMode === "easy") handleAnalyzeFile();
    else handleGetHeaders();
  };

  if (!open) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {uploadStep === "select_mode" && "Bulk Upload - Select Mode"}
              {uploadStep === "mapping" && "Headers Mapping"}
              {uploadStep === "preview" && "Review & Confirm Data"}
              {uploadStep === "summary" && "Upload Summary"}
            </h5>

            <button type="button" className="btn-close" onClick={closeAll}></button>
          </div>

          <div className="modal-body">
            {uploadStep === "select_mode" && (
              <div className="text-center">
                <p className="lead">Choose your upload method.</p>

                <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 my-4">
                  <div
                    className={`card p-3 text-center w-100 ${
                      uploadMode === "easy" ? "border-primary border-2 shadow-sm" : ""
                    }`}
                    style={{ cursor: "pointer", maxWidth: 320 }}
                    onClick={() => setUploadMode("easy")}
                  >
                    <div className="card-body">
                      <h5>
                        <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                        Easy Mode
                      </h5>
                      <p className="text-muted small mb-0">
                        Use pre-defined template for a straightforward process.
                      </p>
                    </div>
                  </div>

                  <div
                    className={`card p-3 text-center w-100 ${
                      uploadMode === "advanced" ? "border-primary border-2 shadow-sm" : ""
                    }`}
                    style={{ cursor: "pointer", maxWidth: 320 }}
                    onClick={() => setUploadMode("advanced")}
                  >
                    <div className="card-body">
                      <h5>
                        <i className="bi bi-gear-wide-connected me-2"></i>
                        Advanced Mode
                      </h5>
                      <p className="text-muted small mb-0">
                        Upload your own file & map columns yourself.
                      </p>
                    </div>
                  </div>
                </div>

                {uploadMode === "easy" && (
                  <>
                    <div
                      className="alert alert-secondary p-3 my-4 mx-auto"
                      style={{ maxWidth: 700, textAlign: "left" }}
                    >
                      <h6 className="alert-heading fw-bold">
                        <i className="bi bi-info-circle-fill me-2"></i>Easy Mode
                        Instructions
                      </h6>
                      <ul className="small mb-0">
                        <li className="mb-2">
                          <strong>1. Download template:</strong>{" "}
                          <a
                            href={template}
                            download="Bulk_Upload_Template.xlsx"
                            className="btn btn-sm btn-success ms-2"
                          >
                            <i className="bi bi-download me-2"></i>Download
                          </a>
                        </li>
                        <li className="mb-2">
                          <strong>2. Only `phone_number_full` required</strong>
                        </li>
                        <li className="mb-2">
                          <strong>3. Country Code is mandatory</strong>
                        </li>
                        <li>
                          ✅ <code>+60123456789</code>, <code>60123456789</code> | ❌{" "}
                          <code>0123456789</code>
                        </li>
                      </ul>
                    </div>
                    <hr className="my-4" />
                  </>
                )}

                <div className="mx-auto" style={{ maxWidth: 700 }}>
                  <label className="form-label">
                    Select your{" "}
                    <strong>{uploadMode === "easy" ? "template" : "Excel/CSV"}</strong>{" "}
                    file:
                  </label>
                  <input
                    className="form-control"
                    type="file"
                    onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                    accept=".xlsx,.xls,.csv"
                  />
                </div>
              </div>
            )}

            {uploadStep === "mapping" && (
              <div className="container-fluid">
                <p className="text-muted text-start">
                  Match columns from your file to required system fields.
                </p>

                <div className="my-4 text-start">
                  <strong>1. How is your phone number formatted?</strong>

                  <div className="accordion mt-2" id="phoneFormatAccordion">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          className={`accordion-button ${
                            phoneFormat !== "single" ? "collapsed" : ""
                          }`}
                          type="button"
                          onClick={() => setPhoneFormat("single")}
                        >
                          In a single column (<strong>e.g., +60123456789</strong>)
                        </button>
                      </h2>
                      <div
                        className={`accordion-collapse collapse ${
                          phoneFormat === "single" ? "show" : ""
                        }`}
                      >
                        <div className="accordion-body">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <label className="form-label mb-0">Full Phone *</label>
                            </div>
                            <div className="col-8">
                              <Select
                                options={fileHeaders.map((h) => ({ value: h, label: h }))}
                                isClearable
                                placeholder="Select column..."
                                onChange={(s) =>
                                  setFieldMapping((p) => ({
                                    ...p,
                                    country: "",
                                    phone: "",
                                    phone_number_full: s ? s.value : "",
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <button
                          className={`accordion-button ${
                            phoneFormat !== "separate" ? "collapsed" : ""
                          }`}
                          type="button"
                          onClick={() => setPhoneFormat("separate")}
                        >
                          In two columns (<strong>Country Code | Phone</strong>)
                        </button>
                      </h2>
                      <div
                        className={`accordion-collapse collapse ${
                          phoneFormat === "separate" ? "show" : ""
                        }`}
                      >
                        <div className="accordion-body">
                          <div className="row align-items-center mb-2">
                            <div className="col-4">
                              <label className="form-label mb-0">Country *</label>
                            </div>
                            <div className="col-8">
                              <Select
                                options={fileHeaders.map((h) => ({ value: h, label: h }))}
                                isClearable
                                placeholder="Select country column..."
                                onChange={(s) =>
                                  setFieldMapping((p) => ({
                                    ...p,
                                    phone_number_full: "",
                                    country: s ? s.value : "",
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="row align-items-center">
                            <div className="col-4">
                              <label className="form-label mb-0">Phone *</label>
                            </div>
                            <div className="col-8">
                              <Select
                                options={fileHeaders.map((h) => ({ value: h, label: h }))}
                                isClearable
                                placeholder="Select phone column..."
                                onChange={(s) =>
                                  setFieldMapping((p) => ({
                                    ...p,
                                    phone_number_full: "",
                                    phone: s ? s.value : "",
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-warning small mt-4">
                      <strong>
                        *Note: Phone {phoneFormat === "separate" && "and Country "}are
                        required
                      </strong>
                    </div>
                  </div>
                </div>

                <hr />

                <div className="row g-3 mt-3 text-start">
                  <strong>2. Optional columns</strong>

                  {[
                    { field: "name", label: "Full Name" },
                    { field: "status", label: "Status" },
                    { field: "remark", label: "Remark" },
                    { field: "followUpDate", label: "Follow Up Date" },
                    { field: "followUpTime", label: "Follow Up Time" },
                  ].map(({ field, label }) => (
                    <div className="col-md-12" key={field}>
                      <div className="row align-items-center">
                        <div className="col-4">
                          <label className="form-label mb-0">{label}</label>
                        </div>
                        <div className="col-8">
                          <Select
                            options={fileHeaders.map((h) => ({ value: h, label: h }))}
                            isClearable
                            onChange={(selected) =>
                              setFieldMapping((prev) => ({
                                ...prev,
                                [field]: selected ? selected.value : "",
                              }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadStep === "preview" && (
              <div>
                <div className="alert alert-info">
                  <p className="mb-1">
                    <strong>Review your data below.</strong> Errors and duplicates
                    are shown first.
                  </p>
                  <ul className="small mb-0">
                    <li>Rows with errors can be edited and fixed directly.</li>
                    <li>
                      For duplicates, choose <strong>Skip</strong> or{" "}
                      <strong>Update</strong>.
                    </li>
                  </ul>
                </div>

                <div className="d-flex justify-content-end gap-3 small mb-2">
                  <span>
                    <i className="bi bi-check-circle-fill text-success"></i> New:{" "}
                    <strong>{previewCounts.new}</strong>
                  </span>
                  <span>
                    <i className="bi bi-exclamation-triangle-fill text-warning"></i>{" "}
                    Duplicates: <strong>{previewCounts.duplicate}</strong>
                  </span>
                  <span>
                    <i className="bi bi-x-circle-fill text-danger"></i> Errors:{" "}
                    <strong>{previewCounts.error}</strong>
                  </span>
                </div>

                <div className="table-responsive" style={{ maxHeight: "60vh", fontSize: "0.85rem" }}>
                  <table className="table table-sm table-hover table-bordered">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Country</th>
                        <th>Phone</th>
                        <th>New Status</th>
                        <th>Remark</th>
                        <th>Action</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((item, index) => (
                        <tr key={item.id || index}>
                          <td>
                            {item.status === "new" && (
                              <span className="badge bg-success-subtle text-success-emphasis">
                                New
                              </span>
                            )}
                            {item.status === "duplicate" && (
                              <span className="badge bg-warning-subtle text-warning-emphasis">
                                Duplicate
                              </span>
                            )}
                            {item.status === "error" && (
                              <span
                                className="badge bg-danger-subtle text-danger-emphasis"
                                title={item.errorReason}
                              >
                                Error
                              </span>
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={item.editData.name || ""}
                                onChange={(e) => handlePreviewRowChange(index, "name", e.target.value)}
                              />
                            ) : (
                              item.newData.name
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={item.editData.country || ""}
                                onChange={(e) => handlePreviewRowChange(index, "country", e.target.value)}
                              />
                            ) : (
                              `+${item.newData.country}`
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={item.editData.phone || ""}
                                onChange={(e) => handlePreviewRowChange(index, "phone", e.target.value)}
                              />
                            ) : (
                              item.newData.phone
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <select
                                className="form-select form-select-sm"
                                value={item.editData.status || "No Status"}
                                onChange={(e) => handlePreviewRowChange(index, "status", e.target.value)}
                              >
                                <option value="No Status">No Status</option>
                                <option value="Follow Up">Follow Up</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Booking">Booking</option>
                                <option value="Closed">Closed</option>
                              </select>
                            ) : (
                              item.newData.status
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={item.editData.remark || ""}
                                onChange={(e) => handlePreviewRowChange(index, "remark", e.target.value)}
                              />
                            ) : (
                              <span className="d-inline-block text-truncate" style={{ maxWidth: 160 }} title={item.newData.remark}>
                                {item.newData.remark}
                              </span>
                            )}
                          </td>

                          <td>
                            {item.status === "duplicate" ? (
                              <select
                                className="form-select form-select-sm"
                                value={item.action}
                                onChange={(e) => handleDuplicateActionChange(index, e.target.value)}
                                disabled={editingPreviewRowId === item.id}
                              >
                                <option value="skip">Skip</option>
                                <option value="update">Update</option>
                              </select>
                            ) : (
                              <span className="text-muted small">
                                {item.status === "error" ? "Fix to proceed" : "Will be added"}
                              </span>
                            )}
                          </td>

                          <td>
                            {editingPreviewRowId === item.id ? (
                              <div className="btn-group">
                                <button className="btn btn-sm btn-success" onClick={() => handleSavePreviewRow(index)}>
                                  Save
                                </button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingPreviewRowId(null)}>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button className="btn btn-sm btn-outline-primary" onClick={() => setEditingPreviewRowId(item.id)}>
                                <i className="bi bi-pencil-fill"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {uploadStep === "summary" && finalReport && (
              <div className="text-center">
                <h4 className="text-success">Upload Complete!</h4>
                <p className="lead">{finalReport.message}</p>
                <ul className="list-group">
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Successfully Added
                    <span className="badge bg-success rounded-pill">
                      {finalReport.successfullyAdded}
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Successfully Updated
                    <span className="badge bg-primary rounded-pill">
                      {finalReport.successfullyUpdated}
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    Skipped (Duplicates or Errors)
                    <span className="badge bg-secondary rounded-pill">
                      {finalReport.skipped}
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {uploadStep === "select_mode" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleProceed}
                disabled={isProcessing || !bulkFile}
              >
                {isProcessing ? "Processing..." : "Next"}
              </button>
            )}

            {uploadStep === "mapping" && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAnalyzeFile}
                disabled={isProcessing}
              >
                {isProcessing ? "Analyzing..." : "Analyze File"}
              </button>
            )}

            {uploadStep === "preview" && (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleExecuteUpload}
                disabled={isProcessing || editingPreviewRowId !== null}
              >
                {isProcessing ? "Saving..." : "Confirm & Save"}
              </button>
            )}

            <button type="button" className="btn btn-secondary" onClick={uploadStep === "summary" ? closeAll : closeAll}>
              {uploadStep === "summary" ? "Done" : "Cancel"}
            </button>
          </div>
        </div>
      </div>

      {/* overlay */}
      <div className="modal-backdrop fade show"></div>
    </div>
  );
}
